import { useStateMachine } from "little-state-machine";
import { useRouter } from "next/router";
import { updateAction } from "../updateAction";
import { useCurrencyState } from "@hooks/useCurrency";
import { useCheckoutDispatch } from "@hooks/useCheckout";
import { trackEvent } from "@lib/track-event";
import { resolveVariant } from "../lib/resolve-variant";
import { resolveBundleDiscount } from "../lib/resolve-bundle-discount";
import { resolveTotalPrice } from "../lib/resolve-total-price";
import { getVariantOptions, getSize, getNavigation } from "../../lib/product-utils";
import formatVariantPrice from "../../lib/format-variant-price";

export function useForm(sections, section, discountMessage, slug) {
  const router = useRouter();
  const { currency } = useCurrencyState();
  const { addBundle } = useCheckoutDispatch();
  const { actions, state } = useStateMachine({ updateAction });

  const isFrench = router.locale === "fr";

  const handleSubmit = (event) => {
    event.preventDefault();

    const items = sections
      .map((section) => {
        if (state[`${section.key}-variant`]) {
          return {
            variant: state[`${section.key}-variant`],
            quantity: 1,
            specialNotes: state[`${section.key}-productGroup`]?.specialNotes,
          };
        }
        return null;
      })
      .filter((item) => item !== null);

    const bundle = sections
      .map((section) => {
        if (state[`${section.key}-variant`]) {
          return {
            variant: state[`${section.key}-variant`],
            product: state[`${section.key}-product`],
            slug: slug,
          };
        }
        return null;
      })
      .filter((item) => item !== null);

    trackEvent("select_content", { bundle });

    addBundle(items);
  };

  let variants = null;
  let product = null;
  product = state[`${section.key}-product`];
  variants = product.productReferenceV2.variants;
  let variant = variants[0];
  if (state[`${section.key}-variant`]) {
    variant = state[`${section.key}-variant`];
  }

  let { price, compareAtPrice, amount } = formatVariantPrice(variant, currency, 1);

  if (discountMessage) {
    const discount = discountMessage.match(/\d+(\%)/g);
    if (discount) {
      compareAtPrice = price;

      price = resolveBundleDiscount(amount, parseFloat(discount[0]) / 100.0, currency);
    }
  }

  const { totalPrice, totalCompareAtPrice } = resolveTotalPrice(
    sections,
    discountMessage,
    state,
    currency
  );

  const navigation = getNavigation(state[`${section.key}-productGroup`], product, [], isFrench);
  const options = getVariantOptions(variants, variant.selectedOptions);

  const handleOptionChange = (e) => {
    const size = e.value;
    const option = e.option;
    const updatedVariant = resolveVariant(variants, { size, option });
    const updatedState = {};
    updatedState[`${section.key}-variant`] = updatedVariant;
    actions.updateAction(updatedState);
  };

  const handleNavigationChange = (e) => {
    const newProduct = state[`${section.key}-productGroup`].shopifyProducts.find(
      (p) => p.productReferenceV2.id === e.id
    );
    const updatedVariant = resolveVariant(newProduct.productReferenceV2.variants, {
      size: getSize(variant),
      option: "size",
    });
    const updatedState = {};
    updatedState[`${section.key}-product`] = newProduct;
    updatedState[`${section.key}-variant`] = updatedVariant;
    actions.updateAction(updatedState);
  };

  return {
    product,
    price,
    compareAtPrice,
    totalPrice,
    totalCompareAtPrice,
    navigation,
    options,
    handleOptionChange,
    handleNavigationChange,
    handleSubmit,
  };
}
